// profilerAuto.js - Automatic Vue/Vuex instrumentation for Profiler
import profiler from '@/globals/Profiler';

// --- Vuex plugin for actions, mutations, getters ---
function profilerAutoVuexPlugin(store) {
    // Actions
    store._actions && Object.keys(store._actions).forEach(type => {
        store._actions[type].forEach((orig, idx, arr) => {
            arr[idx] = function wrappedAction(payload) {
                profiler.enter('VuexAction', 'Vuex', type);
                try {
                    return orig.call(this, payload);
                } finally {
                    profiler.exit();
                }
            };
        });
    });
    // Mutations
    store._mutations && Object.keys(store._mutations).forEach(type => {
        store._mutations[type].forEach((orig, idx, arr) => {
            arr[idx] = function wrappedMutation(payload) {
                profiler.enter('VuexMutation', 'Vuex', type);
                try {
                    return orig.call(this, payload);
                } finally {
                    profiler.exit();
                }
            };
        });
    });
    // Getters (wrap only root-level, not dependencies)
    if (store.getters) {
        Object.keys(store.getters).forEach(key => {
            const desc = Object.getOwnPropertyDescriptor(store.getters, key);
            if (desc && typeof desc.get === 'function') {
                const origGet = desc.get;
                Object.defineProperty(store.getters, key, {
                    get() {
                        profiler.enter('VuexGetter', 'Vuex', key);
                        try {
                            return origGet.call(this);
                        } finally {
                            profiler.exit();
                        }
                    },
                    configurable: true,
                    enumerable: true
                });
            }
        });
    }
}

// --- Vue global mixin for methods, computed, lifecycle ---
const profilerAutoVueMixin = {
    beforeCreate() {
        const compName = this.$options.name || 'AnonymousComponent';
        // Methods
        if (this.$options.methods) {
            Object.keys(this.$options.methods).forEach(key => {
                const orig = this.$options.methods[key];
                if (typeof orig === 'function' && !orig.__isProfiled) {
                    this.$options.methods[key] = function(...args) {
                        profiler.enter('Method', compName, key);
                        try {
                            return orig.apply(this, args);
                        } finally {
                            profiler.exit();
                        }
                    };
                    this.$options.methods[key].__isProfiled = true;
                }
            });
        }
        // Computed
        if (this.$options.computed) {
            Object.keys(this.$options.computed).forEach(key => {
                const orig = this.$options.computed[key];
                if (typeof orig === 'function' && !orig.__isProfiled) {
                    this.$options.computed[key] = function(...args) {
                        profiler.enter('Computed', compName, key);
                        try {
                            return orig.apply(this, args);
                        } finally {
                            profiler.exit();
                        }
                    };
                    this.$options.computed[key].__isProfiled = true;
                } else if (orig && typeof orig.get === 'function' && !orig.get.__isProfiled) {
                    const origGet = orig.get;
                    this.$options.computed[key] = {
                        get: function(...args) {
                            profiler.enter('Computed', compName, key);
                            try {
                                return origGet.apply(this, args);
                            } finally {
                                profiler.exit();
                            }
                        },
                        set: orig.set
                    };
                    this.$options.computed[key].get.__isProfiled = true;
                }
            });
        }
        // Watchers (wrap handler)
        if (this.$options.watch) {
            Object.keys(this.$options.watch).forEach(key => {
                const orig = this.$options.watch[key];
                if (typeof orig === 'function' && !orig.__isProfiled) {
                    this.$options.watch[key] = function(...args) {
                        profiler.enter('Watcher', compName, key);
                        try {
                            return orig.apply(this, args);
                        } finally {
                            profiler.exit();
                        }
                    };
                    this.$options.watch[key].__isProfiled = true;
                } else if (Array.isArray(orig)) {
                    this.$options.watch[key] = orig.map(fn => {
                        if (typeof fn === 'function' && !fn.__isProfiled) {
                            const wrapped = function(...args) {
                                profiler.enter('Watcher', compName, key);
                                try {
                                    return fn.apply(this, args);
                                } finally {
                                    profiler.exit();
                                }
                            };
                            wrapped.__isProfiled = true;
                            return wrapped;
                        }
                        return fn;
                    });
                }
            });
        }
    },
    // Lifecycle hooks
    created() { profiler.enter('Lifecycle', this.$options.name || 'AnonymousComponent', 'created'); profiler.exit(); },
    mounted() { profiler.enter('Lifecycle', this.$options.name || 'AnonymousComponent', 'mounted'); profiler.exit(); },
    updated() { profiler.enter('Lifecycle', this.$options.name || 'AnonymousComponent', 'updated'); profiler.exit(); },
    destroyed() { profiler.enter('Lifecycle', this.$options.name || 'AnonymousComponent', 'destroyed'); profiler.exit(); }
};

// --- Export as single object for global use ---
export const profilerAuto = {
    vuex: profilerAutoVuexPlugin,
    mixin: profilerAutoVueMixin
};

// Expose profiler to window for console access
if (typeof window !== 'undefined') {
    window.profiler = profiler;
}
